<script setup>
import { computed, nextTick, onBeforeMount, onMounted, ref, watch } from "vue";
import LoadingText from "./LoadingText.vue";
import { useAuthStore } from "@/stores";
import { storeToRefs } from "pinia";
import LoadingCard from "./LoadingCard.vue";
import { defineModel, defineProps } from "vue";
import { useHomeFeaturedContentStore } from "@/stores/home/useHomeFeaturedContent.store";

const props = defineProps({
  disableMutation: {
    type: Boolean,
    default: false,
  },
  featuredContent: {
    type: Boolean,
    default: false,
  },
});

const homeFeaturedContentStore = useHomeFeaturedContentStore();
const authStore = useAuthStore();

const { initialLoading, showLoadingFullScreen } = storeToRefs(homeFeaturedContentStore);
const { user } = storeToRefs(authStore);

const loading = defineModel({
  default: true,
});
const showWelcome = ref(true);

const disableMutation = computed(() => props.disableMutation);
const featuredContent = computed(() => props.featuredContent);

const handleAutoHideWelcome = async () => {
  await nextTick();
  setTimeout(async () => {
    showWelcome.value = false;
    document.body.style.overflow = "";
  }, 2000);
};

watch(loading, () => {
  if (!disableMutation.value) {
    return;
  }

  handleAutoHideWelcome();
});

watch(initialLoading, (value) => {
  if (!featuredContent.value) {
    return;
  }

  loading.value = value;
});

onBeforeMount(() => {
  document.body.style.overflow = "hidden";
  if (!initialLoading.value && featuredContent.value) {
    loading.value = false;
    showWelcome.value = false;
    document.body.style.overflow = "";
    return;
  }
});

onMounted(async () => {

  await nextTick();
  if (disableMutation.value) {
    return;
  }
  setTimeout(() => {
    loading.value = false;
    handleAutoHideWelcome();
  }, 2000);
});
</script>
<template>
  <Transition name="fade">
    <div
      class="top-0 right-0 w-full h-screen fixed backdrop-blur-md bg-untitled-gray-600 bg-opacity-45 z-[4000] flex flex-col justify-center items-center"
      v-if="showWelcome && showLoadingFullScreen"
    >
      <div
        class="h-screen flex justify-center items-center transition duration-200 ease-out w-full flex-col"
        :class="{
          'bg-untitled-gray-600 backdrop-blur-md bg-opacity-50': !loading,
          'text-white': !loading,
        }"
      >
        <LoadingCard v-if="loading" />
        <LoadingText label="Loading..." class="text-white" v-if="loading" />
        <div>
          <div
            class="px-4 py-2 rounded text-3xl cursor-pointer select-none transition transform duration-500 flex justify-center items-center text-center"
            :class="{
              'lg:scale-150': !loading,
              'lg:-translate-y-4': !loading,
            }"
          >
            {{ loading ? "" : `Hi, ${user.name}!` }}
          </div>
          <div class="w-full flex justify-center items-center">
            <p
              class="text-center mt-4 font-bold text-xl lg:text-2xl transition transform ease-in flex"
              :class="{
                'translate-y-8': loading,
                'opacity-0': loading,
                'duration-1000': !loading,
                'duration-200': loading,
              }"
            >
              {{ loading ? "" : "Preparing your courses now..." }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.8s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
